import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';

import { tpoGenUrls } from 'config/urls';

import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import type {
    FiltersPayload,
    PromoLibraryRequest,
    PromoLibraryResponse,
    PromoLibraryRow,
    SortOrder,
} from '../../types';

interface UsePromoLibraryDataParams {
    /** Backend `library_groups[].key` for the library being viewed. */
    libraryGroup: string;
    attributes: string[];
    kpis: string[];
    filters: FiltersPayload | null;
    searchKeyword: string;
    sortBy: string;
    sortOrder: SortOrder;
    pageSize: number;
}

interface UsePromoLibraryDataResult {
    dataSource: PromoLibraryRow[];
    isPending: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    totalCount: number;
    loadedCount: number;
    error: unknown;
}

const fetchPromoLibraryData = async (
    body: PromoLibraryRequest,
    signal: AbortSignal,
): Promise<PromoLibraryResponse> => {
    let token = '';
    try {
        token = localStorage.getItem('accessToken') ?? '';
    } catch {
        // incognito / disabled storage; fall through with an empty token
    }

    const response = await axios(tpoGenUrls.getPromoLibraryData, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
        data: body,
    });
    return response.data;
};

/**
 * Paginated Promo Library grid data. Only the visible KPI columns are requested,
 * so toggling a KPI off stops the backend computing it.
 */
function usePromoLibraryData(params: UsePromoLibraryDataParams): UsePromoLibraryDataResult {
    const { libraryGroup, attributes, kpis, filters, searchKeyword, sortBy, sortOrder, pageSize } = params;

    const requestBase = useMemo<Omit<PromoLibraryRequest, 'offset'>>(
        () => ({
            library_group: libraryGroup,
            attributes,
            kpis,
            filters,
            search_string: searchKeyword,
            sort_by: sortBy,
            sorting_order: sortOrder,
            limit: pageSize,
        }),
        [libraryGroup, attributes, kpis, filters, searchKeyword, sortBy, sortOrder, pageSize],
    );

    const enabled = attributes.length + kpis.length > 0;

    const { data, isPending, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, error } = useInfiniteQuery({
        queryKey: [...tpoGenKeys.promoLibrary(), 'data', requestBase],
        queryFn: ({ signal, pageParam }) =>
            fetchPromoLibraryData({ ...requestBase, offset: pageParam }, signal),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _pages, lastPageParam) => {
            const totalCount = lastPage?.metadata?.total_count ?? 0;
            const nextOffset = lastPageParam + pageSize;
            return nextOffset < totalCount ? nextOffset : undefined;
        },
        enabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: 'always',
    });

    const dataSource = useMemo<PromoLibraryRow[]>(
        () => data?.pages?.flatMap((page) => page.tableData ?? []) ?? [],
        [data],
    );

    return {
        dataSource,
        isPending: enabled ? isPending : false,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage: () => fetchNextPage(),
        totalCount: data?.pages?.[0]?.metadata?.total_count ?? 0,
        loadedCount: dataSource.length,
        error,
    };
}

export default usePromoLibraryData;
